import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import { ArrowLeft, Calendar, CreditCard, CheckCircle, XCircle, Clock, Receipt, IndianRupee } from "lucide-react";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";

const ConsultantSubscription = () => {
    const { consultantId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuthStore();
    const [consultant, setConsultant] = useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [consultantId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [profileRes, subscriptionRes] = await Promise.all([
                axiosInstancev1.get(`/consultants/profile/${consultantId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axiosInstancev1.get(`/consultants/${consultantId}/subscription-history`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            
            setConsultant(profileRes.data?.consultant);
            setSubscriptionHistory(subscriptionRes.data?.history || []);
        } catch (error) {
            console.error("Error fetching subscription data:", error);
            toast.error("Failed to load subscription details");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                </div>
            </Layout>
        );
    }

    const displayName = consultant?.engineer_name || consultant?.engineerName || consultant?.name || "Consultant";
    const activeSubscription = subscriptionHistory.find(sub => sub.status?.toLowerCase() === 'active');

    return (
        <Layout>
            <div className="space-y-4 pb-4">
                {/* Back Button */}
                <button
                    onClick={() => navigate(`/builder/consultant-detail/${consultantId}`)}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span className="text-sm font-medium">Back to Profile</span>
                </button>

                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h1 className="text-xl font-bold text-gray-900">Subscription Details</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Subscription history for {displayName}
                    </p>
                </div>

                {/* Active Subscription */}
                {activeSubscription && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-5">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle className="text-green-600" size={20} />
                                    <h3 className="text-lg font-bold text-gray-900">Active Subscription</h3>
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <CreditCard size={14} className="text-gray-600" />
                                        <span className="text-gray-700">
                                            Plan: <span className="font-semibold">{activeSubscription.plan_name || "N/A"}</span>
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={14} className="text-gray-600" />
                                        <span className="text-gray-700">
                                            Valid Till: <span className="font-semibold">
                                                {new Date(activeSubscription.end_date).toLocaleDateString('en-IN')}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <IndianRupee size={14} className="text-gray-600" />
                                        <span className="text-gray-700">
                                            Amount: <span className="font-semibold">₹{activeSubscription.amount}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="inline-flex px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                                    Active
                                </div>
                                {activeSubscription.invoice_number && (
                                    <p className="text-xs text-gray-600 mt-2">
                                        Invoice: {activeSubscription.invoice_number}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* No Active Subscription */}
                {!activeSubscription && (
                    <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
                        <XCircle className="text-gray-400 mx-auto mb-3" size={40} />
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">No Active Subscription</h3>
                        <p className="text-sm text-gray-500">This consultant doesn't have an active subscription.</p>
                    </div>
                )}

                {/* Subscription History */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-gray-900">Subscription History</h3>
                        <span className="text-xs text-gray-500">
                            {subscriptionHistory.length} record{subscriptionHistory.length !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {subscriptionHistory.length === 0 ? (
                        <div className="text-center py-8">
                            <Receipt className="text-gray-300 mx-auto mb-3" size={40} />
                            <p className="text-sm text-gray-500">No subscription history found</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {subscriptionHistory.map((subscription, index) => (
                                <SubscriptionCard key={index} subscription={subscription} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

const SubscriptionCard = ({ subscription }) => {
    const isActive = subscription.status?.toLowerCase() === 'active';
    const isExpired = subscription.status?.toLowerCase() === 'expired';
    const isPending = subscription.status?.toLowerCase() === 'pending';

    const statusConfig = {
        active: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle, color: "text-green-600" },
        expired: { bg: "bg-red-100", text: "text-red-700", icon: XCircle, color: "text-red-600" },
        pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock, color: "text-yellow-600" }
    };

    const status = isActive ? 'active' : isExpired ? 'expired' : isPending ? 'pending' : 'expired';
    const config = statusConfig[status];
    const StatusIcon = config.icon;

    return (
        <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
            <div className="flex items-start justify-between mb-3">
                <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{subscription.plan_name || "Subscription Plan"}</h4>
                    {subscription.invoice_number && (
                        <p className="text-xs text-gray-500 mt-0.5">Invoice: {subscription.invoice_number}</p>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <StatusIcon size={14} className={config.color} />
                    <span className={`px-2 py-0.5 ${config.bg} ${config.text} text-[10px] font-semibold rounded-full`}>
                        {subscription.status || "N/A"}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                    <p className="text-gray-500 mb-0.5">Start Date</p>
                    <p className="text-gray-900 font-medium">
                        {new Date(subscription.start_date).toLocaleDateString('en-IN')}
                    </p>
                </div>
                <div>
                    <p className="text-gray-500 mb-0.5">End Date</p>
                    <p className="text-gray-900 font-medium">
                        {new Date(subscription.end_date).toLocaleDateString('en-IN')}
                    </p>
                </div>
                <div>
                    <p className="text-gray-500 mb-0.5">Amount</p>
                    <p className="text-gray-900 font-medium">₹{subscription.amount || 0}</p>
                </div>
                {subscription.gst_amount && (
                    <div>
                        <p className="text-gray-500 mb-0.5">GST</p>
                        <p className="text-gray-900 font-medium">₹{subscription.gst_amount}</p>
                    </div>
                )}
                {subscription.total_amount && (
                    <div>
                        <p className="text-gray-500 mb-0.5">Total Amount</p>
                        <p className="text-gray-900 font-semibold">₹{subscription.total_amount}</p>
                    </div>
                )}
                {subscription.payment_date && (
                    <div>
                        <p className="text-gray-500 mb-0.5">Payment Date</p>
                        <p className="text-gray-900 font-medium">
                            {new Date(subscription.payment_date).toLocaleDateString('en-IN')}
                        </p>
                    </div>
                )}
            </div>

            {subscription.razorpay_payment_id && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-[10px] text-gray-500">
                        Payment ID: <span className="text-gray-700 font-mono">{subscription.razorpay_payment_id}</span>
                    </p>
                </div>
            )}
        </div>
    );
};

export default ConsultantSubscription;
